"use client";

import { useEffect, useState } from "react";

import { Select } from "@/components/ui-system";
import { cn } from "@/lib/utils/cn";

import { FLOAT_PRECISION_OPTIONS, type ChannelRow, type FloatPrecision } from "../schema/datalogger.schema";

type ChannelPrecisionModalProps = {
  open: boolean;
  onClose: () => void;
  channel: ChannelRow | null;
  onApply: (precision: FloatPrecision) => void;
};

const control =
  "ui-control w-full rounded-control bg-surface-primary text-foreground shadow-none";

export function ChannelPrecisionModal({ open, onClose, channel, onApply }: ChannelPrecisionModalProps) {
  const [precision, setPrecision] = useState<string>(String(channel?.floatPrecision ?? 2));

  useEffect(() => {
    if (open && channel) {
      setPrecision(String(channel.floatPrecision));
    }
  }, [open, channel]);

  if (!open || !channel) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-control border border-card-border bg-surface-primary shadow-card"
        role="dialog"
        aria-modal
        aria-labelledby="precision-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-card-border bg-muted-bg/30 px-ds-6 py-ds-5">
          <h2 id="precision-modal-title" className="font-sans text-lg font-semibold text-neutral-950 dark:text-foreground">
            Float precision - {channel.name.trim() || channel.channelKey}
          </h2>
          <p className="mt-ds-2 text-caption text-neutral-700 dark:text-muted">Decimal places used for display and export.</p>
        </div>

        <div className="px-ds-6 py-ds-5">
          <div className="rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
            <label className="form-label">Decimals</label>
            <Select
              className={cn(control, "mt-ds-1")}
              value={String(precision)}
              onChange={(e) => setPrecision(e.target.value)}
            >
              {FLOAT_PRECISION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <span className="mt-ds-2 block form-helper">Choose how many decimal places should appear by default in dashboards, exports, and tables.</span>
          </div>
        </div>

        <div className="flex justify-end gap-ds-2 border-t border-card-border bg-muted-bg/30 px-ds-6 py-ds-4">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm !no-underline"
            onClick={() => {
              onApply(Number(precision) as FloatPrecision);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
